import React, { useEffect, useState } from "react";
import { fetchUserAllQualifications } from "../../../services/api/user";
import { useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import useAuth from "../../../hooks/useAuth";
import AddLicenseAndCertificationModal from "../../CompleteYourProfile/components/AddLicenseAndCertificationModal";
import { useParams } from "react-router-dom";
import AddGeneralInsuranceModal from "../../CompleteYourProfile/components/AddGeneralInsuranceModal";
import AddEquipmentModal from "../../CompleteYourProfile/components/AddEquipmentModal";
import AddBackgroundVerificationModal from "../../CompleteYourProfile/components/AddBackgroundVerificationModal";

const QualificationMatcher = ({
  workOrderQualifications,
  setIsAllQualificationsPassed,
  overrideUserId,
}) => {
  const { id: workOrderId } = useParams();
  const { authRole, auth } = useAuth();
  const dispatch = useDispatch();

  const isFreelanceCompanyOwner =
    auth?.user?.role?.role === "FREELANCER" && auth?.user?.company?.ownerId === auth?.user?.id;
  const isOwnQualifications = !overrideUserId && !isFreelanceCompanyOwner;
  const showStatus = authRole === "FREELANCER" && (isOwnQualifications || !!overrideUserId);
  const showAction = authRole === "FREELANCER" && isOwnQualifications;
  const [isAddEquipmentModalOpen, setIsAddEquipmentModalOpen] = useState(false);
  const [isAddBackgrounVerificationModalOpen, setIsAddBackgrounVerificationModalOpen] =
    useState(false);
  const [selectedQualificationTypeId, setSelectedQualificationTypeId] = useState(null);
  const [isAddLicenseAndCertificationModalOpen, setIsAddLicenseAndCertificationModalOpen] =
    useState(false);
  const [isAddInsuranceModalOpen, setIsAddInsuranceModalOpen] = useState(false);
  const [formattedQualfications, setFormattedQualfications] = useState([]);
  const [qualificationSubTypeId, setQualificationSubTypeId] = useState(null);
  const {
    loading: isUserAllQualificationsApiLoading,
    error: isUserAllQualificationsApiError,
    refetch: refetchUserAllQualifications,
    data: userAllQualificationsData,
  } = useQuery({
    queryKey: ["userAllQualificationsData", workOrderId, overrideUserId],
    queryFn: () => fetchUserAllQualifications(overrideUserId),
    enabled: !!workOrderId,
  });

  useEffect(() => {
    const matchedQualifications = matchQualifications(
      workOrderQualifications,
      userAllQualificationsData?.data,
    );
    setFormattedQualfications(matchedQualifications);

    if (setIsAllQualificationsPassed) {
      // Check if all qualifications are passed
      const allPassed = matchedQualifications?.every((q) => q.status);

      setIsAllQualificationsPassed(allPassed);
    }
  }, [userAllQualificationsData]);

  // Match qualifications
  const matchQualifications = (workOrderQualifications, userQualifications) => {
    const userQualificationIds = new Set(userQualifications?.map((q) => q.qualificationSubTypeId));

    return workOrderQualifications?.map((qualification) => ({
      ...qualification,
      status: userQualificationIds.has(qualification.id) ? true : false,
    }));
  };

  const addMissingQualification = async (qualification) => {
    setQualificationSubTypeId(qualification?.id);
    setSelectedQualificationTypeId(qualification?.qualificationType?.id);
    if (
      qualification.qualificationType.name.toLowerCase() === "certification" ||
      qualification.qualificationType.name.toLowerCase() === "licences"
    ) {
      setIsAddLicenseAndCertificationModalOpen(true);
      return;
    }
    if (qualification.qualificationType.name.toLowerCase() === "insurance") {
      setIsAddInsuranceModalOpen(true);
      return;
    }
    if (qualification.qualificationType.name.toLowerCase() === "equipment") {
      setIsAddEquipmentModalOpen(true);
      return;
    }
    if (qualification.qualificationType.name.toLowerCase() === "background check") {
      setIsAddBackgrounVerificationModalOpen(true);
      return;
    }
  };
  return (
    <div className="w-full">
      <table className="">
        <thead className="table w-full">
          <tr className="bg-primary text-white w-full">
            <th className="px-4 py-2 w-1/6 text-left">
              <p className="text-white">Type</p>
            </th>
            <th className="px-4 py-2 w-2/6 text-left">
              <p className="text-white">Qualification</p>
            </th>
            {showStatus && (
              <th className="px-4 py-2 w-1/6 text-left">
                <p className="text-white"> Status</p>
              </th>
            )}
            {showAction && (
              <th className="px-4 py-2 w-2/5 text-left">
                <p className="text-white"> Action</p>
              </th>
            )}
          </tr>
        </thead>
        <tbody className="table">
          {formattedQualfications?.map((qualification) => (
            <tr className="odd:bg-white even:bg-gray-100 border-b " key={qualification.id}>
              <td className="px-4 py-2 w-1/6">
                <p className="text-primary cursor-default	">
                  {qualification?.qualificationType?.name}
                </p>
              </td>
              <td className="px-4 py-2 w-2/6">
                <p className="cursor-default">{qualification?.name}</p>
              </td>
              {showStatus && (
                <td className="px-4 py-2 w-1/6">
                  <p
                    className={`cursor-default ${
                      qualification.status ? "text-green-700" : "text-red-600"
                    }`}
                  >
                    {qualification.status ? "Passed" : "Missing"}
                  </p>
                </td>
              )}
              {showAction && (
                <td className="px-4 py-2 w-2/5">
                  {!qualification.status && (
                    <p
                      className="text-primary cursor-pointer inline"
                      onClick={() => addMissingQualification(qualification)}
                    >
                      Add {qualification?.qualificationType?.name} to profile
                    </p>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {isAddLicenseAndCertificationModalOpen && (
        <AddLicenseAndCertificationModal
          open={isAddLicenseAndCertificationModalOpen}
          close={setIsAddLicenseAndCertificationModalOpen}
          qualificationSubTypeId={qualificationSubTypeId}
          refetchUserQualificationsByType={refetchUserAllQualifications}
          licencesId={selectedQualificationTypeId}
          certificationId={selectedQualificationTypeId}
        />
      )}
      {isAddInsuranceModalOpen && (
        <AddGeneralInsuranceModal
          open={isAddInsuranceModalOpen}
          close={setIsAddInsuranceModalOpen}
          qualificationSubTypeId={qualificationSubTypeId}
          refetchUserQualificationsByType={refetchUserAllQualifications}
          insuranceId={selectedQualificationTypeId}
        />
      )}
      {isAddEquipmentModalOpen && (
        <AddEquipmentModal
          open={isAddEquipmentModalOpen}
          close={setIsAddEquipmentModalOpen}
          refetchUserQualificationsByType={refetchUserAllQualifications}
          qualificationSubTypeId={qualificationSubTypeId}
          equipmentId={selectedQualificationTypeId}
        />
      )}
      {isAddBackgrounVerificationModalOpen && (
        <AddBackgroundVerificationModal
          open={isAddBackgrounVerificationModalOpen}
          close={setIsAddBackgrounVerificationModalOpen}
          qualificationSubTypeId={qualificationSubTypeId}
          refetchUserQualificationsByType={refetchUserAllQualifications}
          backgroundCheckId={selectedQualificationTypeId}
        />
      )}
    </div>
  );
};

export default QualificationMatcher;
